import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, MapPin, X } from 'lucide-react';
import { NavigationOverlay } from '@/components/navigation/navigation-overlay';
import { ActionErrorModal } from '@/components/status/action-error-modal';
import { RecordingAlarmModal } from '@/components/status/recording-alarm-modal';
import { WarningStack } from '@/components/status/warning-stack';
import { DashboardSidebar } from '@/components/layout/dashboard-sidebar';
import { DashboardMiddle } from '@/components/layout/dashboard-middle';
import { MobileShell } from '@/components/layout/mobile-shell';
import { MobileJoystickOverlay } from '@/components/layout/mobile-joystick-overlay';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useRobotRealtime } from '@/hooks/useRobotRealtime';
import { useRobotStore } from '@/stores/useRobotStore';
import { NavPill } from '@/components/layout/nav-pill';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import DashboardPage from '@/features/dashboard/dashboard-page';
import MetricsPage from '@/features/metrics/metrics-page';
import SchedulingPage from '@/features/scheduling/scheduling-page';
import EventLogPage from '@/features/event-log/event-log-page';
import { useEffectiveOrgId } from '@/stores/useOrgStore';

export function AppShell() {
  useRobotRealtime();
  const isMobile = useIsMobile();
  const fleetLoading = useRobotStore((s) => s.fleetLoading);
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const onDashboard = pathname === ROUTES.DASHBOARD;
  const onMetrics = pathname === ROUTES.METRICS;
  const onScheduling = pathname === ROUTES.SCHEDULING;
  const onJoystick = pathname === ROUTES.JOYSTICK;
  const isLoggedIn = useEffectiveOrgId() != null;
  const onEventLog = isLoggedIn && pathname === ROUTES.EVENT_LOG;

  // Navigation data lives in the shell so the map blob and waypoints aren't
  // refetched when the user toggles between tabs.
  const { mapInfo, waypoints, mapUrl, loading: mapLoading } = useNavigationData();

  const [showMap, setShowMap] = useState(false);
  const [locModalDismissed, setLocModalDismissed] = useState(false);

  const navPose = useRobotStore((s) => s.navPose);
  const navPath = useRobotStore((s) => s.navPath);
  const positionKnown = useRobotStore((s) => s.positionKnown);
  const localizationDone = useRobotStore((s) => s.localizationDone);
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const warnings = useRobotStore((s) => s.warnings);
  const lastFullStatusAt = useRobotStore((s) => s.lastFullStatusAt);
  const estopActive = useRobotStore((s) => s.estopActive);

  // Modal triggers only on 5s of no full_status (not on disconnect, not on
  // initial load before the first message).
  const [connectionStale, setConnectionStale] = useState(false);

  useEffect(() => {
    if (lastFullStatusAt == null) return;
    setConnectionStale(false);
    const id = setTimeout(() => setConnectionStale(true), 5000);
    return () => clearTimeout(id);
  }, [lastFullStatusAt]);

  const navPayload = useMemo(() => {
    const navHb = heartbeats.find((hb) => hb.heartbeat_id === 'navigation');
    return (navHb?.payload ?? {}) as {
      verify_status?: string;
      verify_error?: string | null;
      loc_reason?: string | null;
    };
  }, [heartbeats]);
  const verifyRunning = navPayload.verify_status === 'running';
  // Why the position is unknown: the ICP localizer's reason, else the verify's error.
  const locReason = navPayload.loc_reason ?? navPayload.verify_error ?? null;

  const locFailed = localizationDone === true && positionKnown === false;
  useEffect(() => {
    if (!locFailed) setLocModalDismissed(false);
  }, [locFailed]);
  const showLocModal = locFailed && !locModalDismissed && !showMap;

  // /joystick is a QR-friendly direct link to the mobile joystick view.
  // It takes over the whole shell on every device; close returns to /.
  // Placed *after* every hook above so navigating between / and
  // /joystick doesn't trip the hooks-rule via a conditional early return.
  if (onJoystick) {
    return (
      <>
        <MobileJoystickOverlay onClose={() => navigate(ROUTES.DASHBOARD)} />
        <ActionErrorModal />
      <RecordingAlarmModal />
      </>
    );
  }

  return (
    <>
      {isMobile ? (
        <MobileShell
          mapInfo={mapInfo}
          mapUrl={mapUrl}
          mapLoading={mapLoading}
          navPose={navPose}
          navPath={navPath}
          waypoints={waypoints}
        />
      ) : (
      <div className="relative h-full overflow-hidden flex">
        {/* Left half — sidebar + middle pane. Width slides 50% ↔ 0%. */}
        <div
          className={cn(
            'h-full relative overflow-hidden transition-[width] duration-500 ease-in-out flex',
            onDashboard ? 'w-1/2' : 'w-0',
          )}
        >
          {/* Sidebar — minimap, quick actions, robot status, e-stop */}
          {!showMap && (
            <DashboardSidebar
              mapInfo={mapInfo}
              mapUrl={mapUrl}
              mapLoading={mapLoading}
              navPose={navPose}
              navPath={navPath}
              waypoints={waypoints}
              onOpenMap={() => setShowMap(true)}
            />
          )}

          {/* Middle pane — URDF / nav controls / task controls */}
          <div className="relative flex-1 min-w-0 overflow-hidden">
            <DashboardMiddle />

            {/* Verify-localization indicator — centered card, doesn't block input */}
            {verifyRunning && (
              <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
                <div className="bg-surface/90 backdrop-blur-sm border border-border rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3">
                  <Loader2 size={20} className="animate-spin text-accent" />
                  <p className="text-sm font-semibold text-foreground">Verifying Location</p>
                </div>
              </div>
            )}

            {/* Warnings — top right of middle pane */}
            {!showMap && (
              <div className="absolute top-3 right-3 z-20">
                <WarningStack warnings={warnings} />
              </div>
            )}

            {/* Localization failure modal */}
            {showLocModal && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="bg-surface border border-border rounded-2xl shadow-2xl p-5 w-80 flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold text-foreground leading-snug">
                        The robot failed to localize. Please set its position on the map.
                      </p>
                      {locReason && <p className="text-xs text-muted leading-snug">{locReason}</p>}
                    </div>
                    <button
                      onClick={() => setLocModalDismissed(true)}
                      className="shrink-0 p-1 rounded-lg text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
                      aria-label="Dismiss"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {mapInfo && (
                    <button
                      onClick={() => { setShowMap(true); setLocModalDismissed(true); }}
                      className="flex items-center justify-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                    >
                      <MapPin size={15} />
                      Go to map
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Navigation overlay — covers entire left half */}
          {showMap && mapInfo && (
            <NavigationOverlay
              mapInfo={mapInfo}
              mapUrl={mapUrl}
              robotPose={navPose}
              path={navPath}
              waypoints={waypoints}
              locFailed={locFailed}
              onClose={() => setShowMap(false)}
            />
          )}
        </div>

        {/* Right pane — pill header + layered route content. Width slides 50% ↔ 100%. */}
        <div
          className={cn(
            'h-full flex flex-col border-l border-border min-h-0 transition-[width] duration-500 ease-in-out',
            onDashboard ? 'w-1/2' : 'w-full',
          )}
        >
          <div className="shrink-0 flex justify-center px-3 py-3 relative z-[65]">
            <NavPill />
          </div>

          {/* All three pages stay mounted so streams/state survive tab switches —
              opacity toggles which one is visible. */}
          <div className="relative flex-1 min-h-0">
            {fleetLoading ? (
              <div className="flex flex-col gap-4 p-6 animate-pulse">
                <div className="h-8 w-48 rounded-2xl bg-surface" />
                <div className="h-48 rounded-2xl bg-surface" />
                <div className="h-32 rounded-2xl bg-surface" />
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    'absolute inset-0 transition-opacity duration-500',
                    onDashboard ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <DashboardPage />
                </div>
                <div
                  className={cn(
                    'absolute inset-0 transition-opacity duration-500',
                    onMetrics ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <MetricsPage />
                </div>
                <div
                  className={cn(
                    'absolute inset-0 transition-opacity duration-500',
                    onScheduling ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <SchedulingPage />
                </div>
                {isLoggedIn && (
                  <div
                    className={cn(
                      'absolute inset-0 transition-opacity duration-500',
                      onEventLog ? 'opacity-100' : 'opacity-0 pointer-events-none',
                    )}
                  >
                    <EventLogPage />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* E-stop blur overlay — when the kill switch is engaged, blur the whole
          UI and instruct the user to release it. The Reset E-Stop button in
          DashboardMiddle is bumped to z-[80] so it pokes through this z-[70]
          backdrop and remains the only interactive element. */}
      {estopActive && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-md">
          <div className="bg-surface border-2 border-orange-500 rounded-2xl shadow-2xl p-10 w-[44rem] max-w-[90vw] flex flex-col gap-4 items-center">
            <p className="text-3xl font-bold text-orange-500 leading-snug">
              Emergency Stop Activated
            </p>
            <p className="text-lg text-foreground leading-relaxed text-center">
              Release the emergency stop to continue.
            </p>
          </div>
        </div>
      )}
      {connectionStale && !onMetrics && !onScheduling && !onEventLog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-2xl shadow-2xl p-7 w-[40rem] max-w-[90vw] flex flex-col gap-3 items-center">
            <p className="text-base font-semibold text-foreground leading-snug">
              Unable to connect the robot
            </p>
            <p className="text-sm text-muted leading-relaxed text-center">
              Waiting for status updates…
            </p>
          </div>
        </div>
      )}
      <ActionErrorModal />
      <RecordingAlarmModal />
    </>
  );
}
